"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/* ============================================================
   TOMBOL UNDUH PDF - untuk bilah atas lightbox EdukasiContent

   ============================================================
   ⚠️ BACA DULU: DUA CARA, DAN YANG PERTAMA LEBIH BAIK

   Komponen ini punya dua jalur, dipilih otomatis:

   1. KALAU materi punya field `pdf` -> berkas itu langsung diunduh.
      Tidak ada pengolahan apa pun di browser.

   2. KALAU TIDAK ADA -> PDF dirakit di browser dari berkas .png di
      `halaman`.

   Jalur 2 berjalan, tapi hasilnya TIDAK SEBAIK jalur 1, dan alasannya
   perlu Anda tahu sebelum memilih:

   - Berkas di public/images/edukasi-kesehatan/ adalah gambar untuk
     layar. Kalau lebarnya sekitar 1000-1500px, dicetak di A4 hasilnya
     kira-kira 90-130 dpi. Cukup dibaca di layar, terlihat kasar
     dicetak - padahal leaflet dan flyer justru sering dicetak ulang
     oleh puskesmas atau kader posyandu.
   - Teks di dalamnya jadi gambar, bukan teks. Tidak bisa dicari, tidak
     bisa disalin, dan tidak terbaca oleh pembaca layar.
   - Ukuran berkasnya membengkak. PNG berisi foto (seperti tangan
     memegang model rahim di poster Mioma) jauh lebih besar daripada
     JPEG atau PDF asli.

   Materi ini hampir pasti dibuat di Canva atau Illustrator, dan
   di situ ADA berkas PDF aslinya. Minta ke tim marketing, simpan di
   public/pdf/edukasi-kesehatan/, lalu tambahkan satu baris di tiap
   materi pada data.js:

       pdf: "/pdf/edukasi-kesehatan/mioma.pdf",

   Begitu field itu ada, komponen ini otomatis memakai jalur 1 -
   tanpa perubahan kode lain. Jalur 2 saya sediakan supaya tombolnya
   bisa hidup sekarang juga, bukan sebagai tujuan akhir.

   ============================================================
   PEMASANGAN

   1. npm install jspdf

      Hanya dipakai jalur 2. Impornya dinamis (await import di dalam
      fungsi), jadi berkasnya baru diunduh browser saat tombol
      ditekan - bundel halaman tidak ikut membesar untuk pengunjung
      yang tidak mengunduh apa pun.

      Kalau SEMUA materi nanti punya field `pdf`, paket ini boleh
      dicopot dan blok jalur 2 dihapus.

   2. Tambahkan kunci berikut ke kamus i18n (id dan en):

        edukasi.unduh        -> "Unduh PDF"            / "Download PDF"
        edukasi.unduhKerja   -> "Menyiapkan..."        / "Preparing..."
        edukasi.unduhGagal   -> "Gagal, coba lagi"     / "Failed, try again"
        edukasi.unduhAria    -> "Unduh {judul} sebagai PDF"
                             /  "Download {judul} as PDF"

      Tanpa ini tombolnya tetap muncul, hanya labelnya terbaca sebagai
      nama kunci.
   ============================================================ */

/* ============================================================
   UKURAN HALAMAN PDF

   PDF memakai satuan titik (point), bukan piksel: 72 titik = 1 inci.
   Kalau lebar gambar 2000px dipakai langsung sebagai 2000 titik,
   halamannya jadi selebar 27 inci - dan pembaca PDF mengecilkannya
   sampai 25% supaya muat di layar. Itu yang terjadi pada unduhan
   sebelumnya.

   DPI di bawah menjawab satu pertanyaan: satu piksel gambar dicetak
   sebesar apa di kertas. Pada 150, gambar 1800px lebarnya menjadi
   12 inci - ukuran leaflet lipat tiga yang wajar.

   ISI GAMBARNYA TIDAK BERUBAH SAMA SEKALI. Yang diatur hanya ukuran
   cetaknya; tidak ada bagian yang dipotong, diperkecil, atau
   ditambahi bingkai.

   NAIKKAN ANGKANYA (mis. 200) kalau hasil cetak terasa terlalu besar
   di kertas. TURUNKAN (mis. 96) kalau terlalu kecil. Angka yang lebih
   tinggi = halaman lebih kecil, kerapatan cetak lebih baik.
   ============================================================ */
const DPI = 150;

const keTitik = (px) => (px * 72) / DPI;

/* Nama berkas dibuat dari judul materi. Titik ikut dibuang - nama
   seperti "GERD 2.0.pdf" membuat sebagian klien email dan Windows
   salah menebak jenis berkasnya. */
function namaBerkas(judul, id) {
  const bersih = String(judul || id || "materi")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

  return `${bersih || "materi"}-rs-jmc.pdf`;
}

/* Gambar dimuat lewat elemen <img> biasa, BUKAN lewat next/image.

   next/image menyajikan versi yang sudah diperkecil dan dikonversi
   ke WebP lewat /_next/image. Itu bagus untuk layar, tapi kalau
   versi itu yang masuk ke PDF, yang terunduh adalah gambar yang
   sudah dua kali diturunkan mutunya. Path mentah di data.js menunjuk
   berkas asli di public/. */
function muatGambar(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.decoding = "sync";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Gagal memuat ${src}`));
    img.src = src;
  });
}

export default function TombolUnduhPDF({ materi, judul, t }) {
  /* "diam" | "kerja" | "gagal" - status ditahan di sini, bukan di
     induk. Lightbox bisa berpindah materi saat unduhan berjalan;
     kalau statusnya di induk, indikator "Menyiapkan..." ikut terbawa
     ke materi berikutnya yang sebenarnya tidak sedang diproses. */
  const [status, setStatus] = useState("diam");

  /* Penanda supaya setState tidak dipanggil setelah komponen dilepas
     (pengguna menutup lightbox sebelum PDF selesai dirakit). Tanpa
     ini React memberi peringatan dan, di React 18 ke atas, pembaruan
     itu terbuang percuma. */
  const hidup = useRef(true);

  useEffect(() => {
    hidup.current = true;
    return () => {
      hidup.current = false;
    };
  }, []);

  const halaman = Array.isArray(materi?.halaman) ? materi.halaman : [];
  const pdfSiap = typeof materi?.pdf === "string" && materi.pdf.length > 0;

  const rakitPDF = useCallback(async () => {
    if (status === "kerja" || halaman.length === 0) return;

    setStatus("kerja");

    try {
      /* Dimuat berbarengan, bukan satu per satu. Materi terpanjang di
         data.js punya 3 halaman; berurutan berarti tiga kali waktu
         tunggu jaringan tanpa alasan. */
      const [{ jsPDF }, gambar] = await Promise.all([
        import("jspdf"),
        Promise.all(halaman.map((h) => muatGambar(h.src))),
      ]);

      let doc = null;

      gambar.forEach((img, i) => {
        const lebarPx = img.naturalWidth;
        const tinggiPx = img.naturalHeight;

        const lebar = keTitik(lebarPx);
        const tinggi = keTitik(tinggiPx);

        /* ORIENTATION HARUS MENGIKUTI BENTUK GAMBAR.

           Ini yang salah di versi sebelumnya. jsPDF MENUKAR SENDIRI
           lebar dan tinggi halaman kalau orientation yang diminta
           tidak cocok dengan isi format: minta "portrait" padahal
           format-nya mendatar, halamannya dibalik jadi tegak. Gambar
           tetap digambar dalam ukuran aslinya di atas halaman yang
           sudah salah bentuk - terpotong di kanan, sisa putih di
           bawah.

           Dengan orientation yang cocok, tidak ada penukaran, dan
           halaman berukuran persis sama dengan gambar.

           Dihitung per halaman, bukan sekali di awal: satu materi
           bisa saja punya sisi depan mendatar dan sisi dalam tegak. */
        const arah = lebar >= tinggi ? "landscape" : "portrait";

        if (i === 0) {
          doc = new jsPDF({
            unit: "pt",
            format: [lebar, tinggi],
            orientation: arah,
            compress: true,
          });
        } else {
          doc.addPage([lebar, tinggi], arah);
        }

        /* Gambar mengisi halaman penuh dari titik 0,0. Tanpa margin -
           margin akan menambah bingkai putih yang tidak ada di
           desain aslinya. */
        doc.addImage(img, "PNG", 0, 0, lebar, tinggi, undefined, "FAST");
      });

      /* Keterangan berkas. Muncul di judul jendela pembaca PDF dan di
         panel Properties - tanpa ini, yang tampil hanya nama berkas.
         Untuk materi yang dibagikan ulang lewat WhatsApp atau email,
         ini satu-satunya penanda asal berkasnya. */
      doc.setProperties({
        title: judul || materi?.id || "Materi Edukasi",
        subject: "Materi edukasi pasien",
        author: "RS Jakarta Medical Center",
        creator: "rsjmc.id",
      });

      /* Dibuka pas satu halaman penuh, bukan pada zoom terakhir yang
         diingat pembaca PDF. Inilah yang membuat berkas kemarin
         terbuka di 25% - halamannya berukuran ribuan titik, jadi
         Chrome mengecilkannya sampai muat. */
      if (typeof doc.setDisplayMode === "function") {
        doc.setDisplayMode("fullpage", "single");
      }

      doc.save(namaBerkas(judul, materi?.id));

      if (hidup.current) setStatus("diam");
    } catch (err) {
      /* Sengaja dicatat ke console. Penyebab paling sering adalah
         satu berkas .png yang namanya tidak cocok dengan data.js -
         dan itu hanya terlihat kalau pesannya menyebut path-nya. */
      console.error("[TombolUnduhPDF]", err);
      if (!hidup.current) return;

      setStatus("gagal");
      /* Kembali ke "diam" setelah 4 detik. Tombol yang menetap di
         keadaan gagal membuat pengunjung mengira tombolnya rusak
         permanen, padahal penyebabnya sering sementara (jaringan
         putus di tengah). */
      setTimeout(() => {
        if (hidup.current) setStatus("diam");
      }, 4000);
    }
  }, [status, halaman, judul, materi]);

  if (halaman.length === 0 && !pdfSiap) return null;

  const label =
    status === "kerja"
      ? t("edukasi.unduhKerja")
      : status === "gagal"
        ? t("edukasi.unduhGagal")
        : t("edukasi.unduh");

  /* SATU KELAS UNTUK DUA ELEMEN. Jalur 1 merender <a download>, jalur
     2 merender <button> - keduanya harus terlihat persis sama, jadi
     kelasnya ditulis sekali di sini.

     Bentuknya mengikuti tombol "Kembali ke daftar" di bilah yang sama:
     latar transparan, teks putih, isian muncul saat hover. Yang
     membedakan hanya ring tipis - cukup untuk menandai bahwa ini
     tindakan, bukan navigasi, tanpa menjadikannya lebih menonjol
     daripada materinya sendiri.

     Latar hijau padat sengaja TIDAK dipakai di sini: satu-satunya
     elemen hijau padat di situs ini adalah "Buat Janji Temu", dan
     menyamakan bobotnya dengan tombol unduh melemahkan keduanya. */
  const kelas =
    "flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg px-2.5 text-[13px] font-semibold text-white/90 ring-1 ring-inset ring-white/20 transition hover:bg-white/10 hover:text-white hover:ring-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-wait disabled:opacity-70 sm:px-3 sm:text-sm";

  const isi = (
    <>
      {status === "kerja" ? (
        /* Lingkaran berputar. animate-spin dimatikan sendiri oleh
           Tailwind pada motion-reduce, jadi tidak perlu ditulis. */
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.25" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ) : status === "gagal" ? (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
          <path d="M12 8v5m0 3.5h.01M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
          <path d="M12 3v11m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}

      {/* Label disembunyikan di ponsel, sama seperti tombol "Kembali
         ke daftar" di sebelah kiri. Di layar 360px, bilah ini harus
         memuat tombol kembali, judul materi, tombol ini, dan tombol
         tutup - judulnya yang paling berhak atas ruang tersisa.
         aria-label di elemen induk menjaga tombolnya tetap terbaca
         pembaca layar meski teksnya tidak tampak. */}
      <span className="hidden sm:inline">{label}</span>
    </>
  );

  const ariaLabel = t("edukasi.unduhAria", { judul });

  /* ---------- JALUR 1: berkas PDF sudah ada ----------
     Tidak ada JavaScript sama sekali. Atribut download bekerja untuk
     berkas satu asal (same-origin), dan public/ selalu satu asal.

     Kalau nanti PDF-nya dipindah ke CDN atau bucket lain, atribut
     download akan DIABAIKAN browser dan berkasnya terbuka di tab baru
     alih-alih terunduh. Kalau itu terjadi, sajikan berkasnya dengan
     header Content-Disposition: attachment dari sisi peladen. */
  if (pdfSiap) {
    return (
      <a href={materi.pdf} download aria-label={ariaLabel} className={kelas}>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
          <path d="M12 3v11m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="hidden sm:inline">{t("edukasi.unduh")}</span>
      </a>
    );
  }

  /* ---------- JALUR 2: dirakit dari gambar ---------- */
  return (
    <button
      type="button"
      onClick={rakitPDF}
      disabled={status === "kerja"}
      aria-label={ariaLabel}
      /* aria-live="polite" pada elemen tombol membuat perubahan
         "Menyiapkan..." -> "Gagal" dibacakan pembaca layar. Tanpa ini,
         pengguna nonvisual menekan tombol dan tidak mendapat kabar apa
         pun sampai berkasnya benar-benar turun. */
      aria-live="polite"
      className={kelas}
      /* Lebar minimum menahan bilah supaya tidak bergeser saat label
         berubah panjang ("Unduh PDF" -> "Menyiapkan..."). Judul materi
         di sebelahnya memakai flex-1, jadi tanpa ini judulnya ikut
         melompat setiap kali status berubah. */
      style={{ minWidth: "2.5rem" }}
    >
      {isi}
    </button>
  );
}
